import React, { useState, useEffect, useContext, useId } from "react";
import moment from "moment";
import {
  Alert,
  Button,
  Container,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { tienePermiso } from "../../../helpers/permisos";
import { formatearFecha } from "../../../helpers/formato";
import { getToken } from "../../../api/Account";
import { addHolidayAsync, deleteHolidayAsync, getHolidaysByYearAsync } from "../../../api/Holiday";
import {
  CargandoTabla,
  EstadoChip,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  CampoFecha,
  DialogoFormulario,
  encabezadoTabla,
  useCargaLocal,
  useConfirmacion,
  useEnvio,
  useEscritura,
} from "../comunes";

const diaDeLaSemana = (fecha) => {
  const m = moment(fecha);
  if (!m.isValid()) return "";
  const dia = m.toDate().toLocaleDateString("es-NI", { weekday: "long" });
  return dia.charAt(0).toUpperCase() + dia.slice(1);
};

const esHoyOFuturo = (fecha) => moment(fecha).isSameOrAfter(moment(), "day");

/** Alta de un feriado. Si es de hoy en adelante, avisa ANTES de guardar que se moverán cuotas. */
const DialogoFeriado = ({ onCerrar, onGuardado }) => {
  const token = getToken();
  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const aviso = useAviso();
  const { guardando, enviar } = useEnvio();
  const id = useId();
  const [fecha, setFecha] = useState(new Date());
  const [descripcion, setDescripcion] = useState("");
  const [intentado, setIntentado] = useState(false);

  const fechaValida = moment(fecha).isValid();
  const errores = {
    fecha: intentado && !fechaValida ? "Elija una fecha válida" : "",
    descripcion: intentado && descripcion.trim() === "" ? "Escriba el nombre del feriado" : "",
  };

  const guardar = () =>
    enviar(async () => {
      setIntentado(true);
      if (!fechaValida || descripcion.trim() === "") return;
      setIsLoading(true);
      try {
        // Igual que antes: la fecha va como YYYY-MM-DD (sin hora) y el mensaje lo arma el servidor.
        const result = await addHolidayAsync(token, {
          fecha: moment(fecha).format("YYYY-MM-DD"),
          description: descripcion.trim(),
        });
        setIsLoading(false);
        if (!result.statusResponse) {
          if (result.error === "Unauthorized") {
            setIsUnautorized(true);
            return;
          }
          aviso.error(result.error || "No se pudo agregar el feriado");
          return;
        }
        onGuardado((result.data && result.data.message) || "Día festivo agregado");
      } catch (e) {
        setIsLoading(false);
        aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
      }
    });

  return (
    <DialogoFormulario
      titulo="Nuevo feriado"
      onCerrar={onCerrar}
      hayCambios={descripcion.trim() !== "" || !moment(fecha).isSame(moment(), "day")}
      guardando={guardando}
      onEnviar={guardar}
      textoEnviar="Agregar feriado"
    >
      <Stack spacing={1} sx={{ mt: 1 }}>
        <CampoFecha
          label="Fecha"
          value={fecha}
          onChange={(nueva) => setFecha(nueva)}
          error={errores.fecha}
          ayuda={fechaValida ? diaDeLaSemana(fecha) : " "}
          disabled={guardando}
          required
        />
        <TextField
          fullWidth
          required
          id={`${id}-descripcion`}
          label="Nombre del feriado"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value.toUpperCase())}
          error={Boolean(errores.descripcion)}
          helperText={errores.descripcion || "Por ejemplo DIA DE LA INDEPENDENCIA"}
          disabled={guardando}
          inputProps={{ autoComplete: "off" }}
        />
        {fechaValida && esHoyOFuturo(fecha) ? (
          <Alert severity="warning">
            Al guardar, las cuotas pendientes de ese día pasan al siguiente día hábil. En los
            créditos diarios también se corren las cuotas que siguen, para que no queden dos el
            mismo día. Si después borra el feriado, las cuotas no vuelven a su fecha.
          </Alert>
        ) : null}
      </Stack>
    </DialogoFormulario>
  );
};

const HolidayList = () => {
  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  const [lista, setLista] = useState([]);
  const [recargar, setRecargar] = useState(false);
  const [agregando, setAgregando] = useState(false);
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  const puedeAgregar = tienePermiso(access, "CONFIG CREATE");
  const puedeBorrar = tienePermiso(access, "CONFIG DELETE");

  const { cargando, error, cargar } = useCargaLocal(() => getHolidaysByYearAsync(token));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setLista(result.data.result || []);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar]);

  const recargarLista = () => setRecargar((actual) => !actual);
  const ordenados = [...lista].sort((a, b) => moment(a.festivo).valueOf() - moment(b.festivo).valueOf());

  const borrar = async (fila) => {
    const ok = await escribir(() => deleteHolidayAsync(token, fila.id), "No se pudo borrar el feriado");
    if (!ok) return;
    recargarLista();
    aviso.exito(`Feriado ${fila.description} borrado`);
  };

  const pedirBorrar = (fila) =>
    pedirConfirmacion({
      titulo: "Borrar feriado",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se borrará ${fila.description} del ${formatearFecha(fila.festivo)}: ese día vuelve a ser hábil para los cobros nuevos.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Las cuotas que se movieron al registrarlo no vuelven a su fecha.
          </Typography>
        </>
      ),
      textoConfirmar: "Borrar feriado",
      peligro: true,
      onConfirmar: () => borrar(fila),
    });

  const botonAgregar = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAgregando(true)}>
      Agregar feriado
    </Button>
  );

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={3} etiqueta="Cargando feriados…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={error === "conexion" ? "No se pudo conectar con el servidor" : "No se pudieron cargar los feriados"}
        onReintentar={recargarLista}
      />
    );
  } else if (ordenados.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="No hay feriados registrados"
        descripcion="En los feriados no se cobran cuotas: el plan de pagos los salta."
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <TableContainer>
        <Table aria-label="Feriados" sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...encabezadoTabla, display: { xs: "none", sm: "table-cell" } }}>Fecha</TableCell>
              <TableCell sx={encabezadoTabla}>Feriado</TableCell>
              {puedeBorrar ? <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenados.map((row) => {
              const pasado = !esHoyOFuturo(row.festivo);
              return (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", display: { xs: "none", sm: "table-cell" } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatearFecha(row.festivo)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {diaDeLaSemana(row.festivo)}
                    </Typography>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {/* En el teléfono la fecha pasa debajo del nombre. */}
                    <Typography variant="body2" sx={{ fontWeight: 700, display: { xs: "block", sm: "none" }, fontVariantNumeric: "tabular-nums" }}>
                      {`${formatearFecha(row.festivo)} · ${diaDeLaSemana(row.festivo)}`}
                    </Typography>
                    {row.description}
                    {pasado ? (
                      <Stack direction="row" sx={{ mt: 0.5 }}>
                        <EstadoChip estado="neutro" label="Ya pasó" />
                      </Stack>
                    ) : null}
                  </TableCell>
                  {puedeBorrar ? (
                    <TableCell sx={{ textAlign: "right" }}>
                      <Tooltip title="Más acciones">
                        <IconButton
                          aria-label={`Más acciones de ${row.description} del ${formatearFecha(row.festivo)}`}
                          aria-haspopup="menu"
                          onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                          sx={{ color: "text.secondary" }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Feriados"
        subtitulo="Días sin cobro de este año en adelante: el plan de pagos los salta"
        icono={<FontAwesomeIcon icon={faCalendarXmark} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>{contenido}</Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirBorrar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Borrar feriado</ListItemText>
        </MenuItem>
      </Menu>

      {agregando ? (
        <DialogoFeriado
          onCerrar={() => setAgregando(false)}
          onGuardado={(mensaje) => {
            aviso.exito(mensaje);
            setAgregando(false);
            recargarLista();
          }}
        />
      ) : null}

      {confirmacion}
    </Container>
  );
};

export default HolidayList;
